import {
	assign,
	find,
	some,
	filter,
	forEach,
	merge
} from "lodash";
import { FORM_PERMISSIONS } from "routes.js";
import {
	FEATURE_TYPES,
	TIME_DISPLAY_OPTIONS,
	ENTITY_TYPES
} from "constants.js";
import { defaultRolePermissions } from "./baseRolePermissions.const";
import { baseEntityPermission } from "./basePermissions.const";
import swal from "sweetalert2";
import moment from "moment";
import CryptoJS from "crypto-js";  // this is bundled in oidc-client
import {
	end as durationEnd,
	parse as parseDuration,
	pattern as durationPattern,
	toSeconds
} from "iso8601-duration";
import {
	DEFAULT_NAMESPACE,
	I18NextContext
} from "./i18nService";
import { globs } from "./globs";
import { isValueNullish } from "../utils/valueUtilities";
import {
	getNewUsageUpdates,
	updateUsage
} from "../utils/dataUsage";

const deps = [];
const DEFAULT_ROLE_CODE = "USER";

function AppState(
	$window,
	$base64,
	$logger,
	$uibModal,
	api,
	loadingService,
	entityManager,
	ruleConfigService,
	$userProfile,
	hotkeys,
	ruleValidator,
	openIdService,
	toastr,
	securityService,
	formService,
	proxyService,
	uiState,
	agreementService,
	campaignService
) {
	const service = this;

	service.reset = reset;
	service.clearSelectedEntity = clearSelectedEntity;
	service.setRecentlyUsed = setRecentlyUsed;
	service.getRecentlyUsed = getRecentlyUsed;
	service.setUnavailable = setUnavailable;
	service.clearUnavailable = clearUnavailable;
	service.updateEntity = updateEntity;
	service.permissions = defaultRolePermissions[DEFAULT_ROLE_CODE];
	service.reloadForm = () => loadForm(service.forms.selected);

	function initProfiles() {
		const currentProfile = $userProfile.getUserProfile();

		if(currentProfile) {
			currentProfile.timeDisplay = currentProfile.timeDisplay || TIME_DISPLAY_OPTIONS.TWELVE_HOUR;
			currentProfile.shortcutsEnabled = currentProfile.shortcutsEnabled || true;

			if(service.forms && service.forms.selected && !currentProfile.formId) {
				currentProfile.formId = service.forms.selected.Id;
			}

			if(currentProfile.shortcutsEnabled === false) {
				hotkeys.pause();
			}

			uiState.wideTableMode = currentProfile.wideTableDisplay === true;

			$userProfile.setUserProfile(currentProfile);
		}
	}

	function loadPermissions(formConfig, apiRoles) {
		const assignedRoleCode = apiRoles.Assigned?.[0].Code;
		const assignedRole = apiRoles.Roles.find(ro => ro.Code === assignedRoleCode);

		const defaultRoleNames = Object.keys(defaultRolePermissions);
		const assignedRoleName = [assignedRole?.Code, assignedRole?.Name]
			.find((roleName) => defaultRoleNames.indexOf(roleName) > -1) ?? DEFAULT_ROLE_CODE;

		service.operatingUserRole = assignedRoleName;

		const defaultPermissions = merge({}, defaultRolePermissions);
		const permissions = merge(defaultPermissions, formConfig.Permissions);

		const webSpecificPermissions = merge({}, formConfig.Permissions?.[assignedRoleName]?.Web ?? {});
		const allUserPermissions = merge({}, permissions[assignedRoleName] ?? {});

		service.permissions = merge({}, allUserPermissions, webSpecificPermissions);

		const irregularEntityTypes = Object.entries(service.permissions.Administration.Entity.Type)
			.filter(([, t]) => Object.keys(ENTITY_TYPES).indexOf(t.Code) < 0);
		irregularEntityTypes.forEach(([key, type]) => {
			type.Code = key;
			const newType = merge({}, baseEntityPermission, type);

			service.permissions.Administration.Entity.Type[key] = newType;
		});

		window.vesl.getPermissions = () => service.permissions;
	}

	function getDefaultState() {
		initProfiles();

		return {
			user: {},
			forms: {
				isDirty: false,
				items: [],
				selected: null,

				setSelected(form) {
					initProfiles();
					this.items = this.items.map(f => {
						return {
							...f,
							selected: f.Id === form?.Id,
							report: {}
						};
					});

					$window.sessionStorage.setItem("forms", angular.toJson(this.items));

					const thisUserProfile = $userProfile.getUserProfile();

					thisUserProfile.formId = form?.Id;

					$userProfile.setUserProfile(thisUserProfile);
				},

				select(form) {
					if(this.selected && form?.Id == this.selected.Id) {
						return Promise.resolve(service.forms.selected);
					}

					this.selected = form;
					this.setSelected(form);

					if(form === undefined) {
						return Promise.resolve(undefined);
					}

					if(form.nomenclature.FormUseAcceptance !== undefined && form.nomenclature.FormUseAcceptance !== "") {
						const userProfile = $userProfile.getUserProfile();

						const hash = CryptoJS.SHA256(form.nomenclature.FormUseAcceptance);
						const userFormHash = userProfile.formAcceptances?.[form.Code];

						if(userFormHash !== undefined && hash.toString(CryptoJS.enc.Hex) === userFormHash) {
							return loadForm(form);
						}

						const modalInstance = $uibModal.open({
							component: "agreement-display-modal",
							size: "lg",
							backdrop: "static",
							keyboard: false,
							windowClass: "vesl-top",
							resolve: {
								nomenclature: () => form.nomenclature,
								formCode: () => form.Code
							}
						});

						return modalInstance.result
							.then((shouldPersistAcceptance) => {
								if(shouldPersistAcceptance) {
									userProfile.formAcceptances = userProfile.formAcceptances ?? {};
									userProfile.formAcceptances[form.Code] = hash.toString(CryptoJS.enc.Hex);
									$userProfile.setUserProfile(userProfile);
								}

								return loadForm(form);
							})
							.catch(() => {
								if(this.items.length === 1) {
									toastr.error("Form acceptance rejected, logging out.", "", {
										timeout: 2000
									});

									return new Promise(() => {
										setTimeout(() => openIdService.logout(), 2000);
									});
								}

								$uibModal.open({
									component: "form-selector-modal",
									backdrop: "static",
									keyboard: false,
									size: "md",
									resolve: {
										mustSelect: () => true,
										disableForm: () => form.Id
									}
								});

								return loadForm(form);
							});
					}

					return loadForm(form);
				},

				selectByFormId(id) {
					const foundForm = this.items.filter((form) => form.Id === id);
					if(foundForm.length === 1) {
						return this.select(foundForm[0]);
					}
					else {
						$logger.warn("Requested to select form that is not available", { form: id });
					}
				}
			},
			associatedEntities: {
				items: [],
				selected: {
					primary: null,
					friendly: null
				}
			},
			isServiceUnavailable: false,
			swalInstance: null,
			staleTimeoutAt: null,
			invalidTimeoutAt: null,
			invalidTimeoutPadded: null
		};
	}

	function setUnavailable(message, isBackgroundRequest = false) {
		if(!service.isServiceUnavailable) {
			swal.fire({
				title: "Please Hold",
				html:
					`${message}
						<br/><br/>
						Do not close or refresh this page${isBackgroundRequest ? "" : ", we will automatically retry your request as soon as possible"}.
						<br/><br/>
						Please be patient...
						<br/><br/>
						<div class="preloader pl-xxl">
							<svg class="pl-circular" viewBox="25 25 50 50">
								<circle class="plc-path" cx="50" cy="50" r="20" />
							</svg>
						</div>`,
				allowOutsideClick: false,
				allowEscapeKey: false,
				allowEnterKey: false,
				showConfirmButton: false,
				showCancelButton: false,
				type: "warning",
				onOpen: () => {
					service.swalInstance = swal;
				}
			});
		}

		service.isServiceUnavailable = true;
	}

	function clearUnavailable() {
		service.isServiceUnavailable = false;
		if(service.swalInstance) {
			service.swalInstance.close();
		}
		service.swalInstance = null;
	}

	function mapBlobIntoNestedDictionary(combinations) {
		const lookup = {};

		combinations.forEach(combo => {
			const parts = combo.split(",");
			let current = lookup;

			for(const part of parts) {
				if(!current[part]) {
					current[part] = {};
				}
				current = current[part];
			}
		});

		return lookup;
	}

	function flattenSections(sections = [], isNested = false) {
		return sections.reduce((allSections, section) => {
			section.$metadata = { ...section.$metadata, IsNested: isNested };
			allSections.push(section);

			allSections.push(...flattenSections(section.Sections, isNested || section.IsMulti === true));

			return allSections;
		}, []);
	}

	function loadForm(form) {
		campaignService.invalidateCampaigns();
		const promise = formService.getFormById(form.Id, form)
			.then(response => {
				if(!agreementService.hasRequiredAgreements) {
					return response;
				}

				return agreementService.presentRequiredAgreements()
					.then(areAllAgreed => {
						if(areAllAgreed === false) {
							this.selected === undefined;
							throw new Error("User did not accept all required agreements");
						}

						return response;
					});
			})
			.then(response => {
				const updatedForm = {
					...form,
					...response
				};

				if(updatedForm.StaleTimeout && durationPattern.test(updatedForm.StaleTimeout)) {
					service.staleTimeoutAt = durationEnd(parseDuration(updatedForm.StaleTimeout));
				}

				if(updatedForm.InvalidTimeout && durationPattern.test(updatedForm.InvalidTimeout)) {
					const TIMEOUT_EXPIRATION_PAD = 0.90;
					const timeoutDuration = parseDuration(updatedForm.InvalidTimeout);
					const paddedDurationInMs = toSeconds(timeoutDuration) * TIMEOUT_EXPIRATION_PAD;
					service.invalidTimeoutAt = durationEnd(timeoutDuration);
					service.invalidTimeoutPadded = new Date(new Date().valueOf() + (paddedDurationInMs*1000));
				}

				updatedForm.Agency.Classifications = response.Agency.Classifications;
				// Keep nested section data on each section, but expose all sections at the form level
				// for legacy consumers that resolve fields by searching form.Sections.
				updatedForm.Sections = flattenSections(updatedForm.Sections);
				forEach(updatedForm.Fields, field => {
					const fullSection = find(updatedForm.Sections, (section) => section.Id === field.Section?.Id);
					field.Section = fullSection;
					field.IsDetail = field.IsDetail ?? (fullSection?.IsMulti || fullSection?.$metadata?.IsNested ? true : undefined);
				});

				mapFeaturesToFields(updatedForm);

				forEach(updatedForm.Seasons, season => {
					forEach(season.Periods, period => {
						period.beginInclusiveMoment = moment(period.BeginInclusive);
						period.endExclusiveMoment = moment(period.EndExclusive);
						period.openRange = moment.range(period.BeginInclusive, period.EndExclusive);
					});
				});

				entityManager.reset();
				entityManager.loadEntityFieldData(updatedForm);
				updatedForm.Rules = ruleValidator.filterToValidRules(updatedForm.Rules, updatedForm.Fields);

				updatedForm.Rules
					.forEach(r => {
						const conditionFields = ruleConfigService.evaluatedConditionFieldIds(r);
						ruleConfigService.assignFieldOrders(r, updatedForm.Fields, conditionFields);
						r.Scope = { Code: ruleConfigService.reportScope(r, updatedForm.Fields) };

						// note that for validation block purposes, the scope may differ from the rule evaluation scope
						r.validationScope = { Code: ruleConfigService.validationScope(r, updatedForm.Fields) };
						r.visibilityScope = { Code: ruleConfigService.getVisibilityRuleScope(r, updatedForm.Fields) };
						r.isAggregate = ruleConfigService.isAggregate(r);

						r.hasAggregation = ruleConfigService.hasAggregation(r);

						ruleConfigService.applyFunctionToAllChildRules(r, (childRule) => {
							if(childRule.Blob?.Binary) {
								const csvText = $base64.decode(childRule.Blob?.Binary);
								childRule.Blob.combinations = csvText.split("\n").map(r => r.replace("\r", ""));
								childRule.Blob.comboDictionary = mapBlobIntoNestedDictionary(childRule.Blob.combinations);
							}
						});
					});

				const translations = formService.buildFormTranslations(updatedForm, "en-us", true);
				const i18n = I18NextContext.Instance.i18n;
				i18n.addResourceBundle("en-us", DEFAULT_NAMESPACE, translations);

				return updatedForm;
			})
			.then(selectedForm => api.get(FORM_PERMISSIONS(selectedForm.Id))
				.then((securityResponse) => {
					loadPermissions(selectedForm, securityResponse);

					selectedForm.Security = assign({}, selectedForm.Security, securityResponse);

					service.forms.selected = selectedForm;
					securityService.init(securityResponse);

					window.vesl && (window.vesl.form = () => { $logger.object(service.forms.selected); });
					window.vesl && (window.vesl.state = () => { $logger.object(service); });

					return selectedForm;
				})
				.then(selected => {
					service.forms.selected = selected;
					return selected;
				})
			)
			.then(selectedForm => {
				proxyService.clearProxies();

				if(!securityService.isPatternAllowed(globs.proxies.byType("*").read))
					return selectedForm;

				return proxyService.loadProxies(selectedForm.Id)
					.then(() => selectedForm);
			})
			.catch((err) => {
				$logger.error("Error loading form", { err });
				service.forms.select(undefined);
			});

		return loadingService.watch(promise, "Loading...");
	}

	function hasValidGeoJson(dataItem) {
		return (
			!!(dataItem.Shape || {}).GeoJson
			|| (
				!!dataItem.Geometries
				&& dataItem.Geometries.length > 0
				&& dataItem.Geometries.some(g =>
					g?.Type?.Code === "GEOJSON"
					&& !isValueNullish(g?.Definition)
					&& (g?.Definition?.type === "Feature" || g?.Definition?.type === "FeatureCollection")
				)
			)
		);
	}

	function mapFeaturesToFields(form) {
		form.Features.map(feat => {
			if(feat.Type == FEATURE_TYPES.filter && feat.Blob) {
				const csvText = $base64.decode(feat.Blob.Binary);
				feat.data = csvText.split("\n");
			}

			if(feat.Type == FEATURE_TYPES.map || feat.Type == FEATURE_TYPES.mapPoint) {
				const fields = filter(form.Fields, (f) => some(f.Data, (d) => hasValidGeoJson(d)));
				feat.data = fields;

				if(feat.Type === FEATURE_TYPES.mapPoint && feat.Blob && feat.Blob.Binary) {
					feat.buoys = JSON.parse($base64.decode(feat.Blob.Binary));
				}
			}
		});
	}

	function updateContacts(original, updated) {
		if(updated.Id === original.Id) {
			original.Contacts = updated.Contacts;
		}
		if(!original.Entities)
			return;
		for(const e of original.Entities) {
			updateContacts(e.Entity, updated);
		}
	}

	function updateEntity(entity) {
		entityManager.updateEntity(entity);

		for(const field of this.forms.selected.Fields) {
			if(!field.EntityQueryConfiguration)
				continue;
			for(const data of field.Data) {
				updateContacts(data, entity);
			}
		}
	}

	function setRecentlyUsed(form, updates, previousValues, usedAt = new Date().toISOString()) {
		getNewUsageUpdates(updates, previousValues).forEach(update => {
			mapRecentlyUsedUpdate(form, update, usedAt);

			// manage-report shallow-copies the form but retains its Fields array.
			// Only update the selected form separately when it owns a distinct field graph.
			if(form !== this.forms.selected && form.Fields !== this.forms.selected.Fields) {
				mapRecentlyUsedUpdate(this.forms.selected, update, usedAt);
			}
		});

		this.selected = form;

		return form;
	}

	function mapRecentlyUsedUpdate(form, update, usedAt) {
		const field = find(form.Fields, { Id: update.FieldId });

		if(field?.IsRecentlyUsedEnabled === true) {
			updateUsage(field.Data, update.EntityId ?? update.DataId, usedAt);
		}
	}

	function getRecentlyUsed() {
		return this.selected;
	}

	function clearSelectedEntity() {
		this.associatedEntities.selected = {
			primary: null,
			friendly: null
		};
	}

	assign(service, getDefaultState());

	function reset() {
		$logger.user(null);

		$window.sessionStorage.clear();

		assign(service, getDefaultState());
	}

	return service;
}
export default angular
	.module("vesl.services.app-state", deps)
	.service("appState", AppState).name;
